import React, { useState, useCallback, useEffect } from "react";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import { Buffer } from "buffer";
import PhoneNumberInput from "../../components/phoneNumberInput";
import PasswordInput from "../../components/passwordInput";
import UserFile from "../../components/userFile";
import LoadingButton from "../../components/loadingButton";
import ErrorMessage from "../../components/errorMessage";
import { dataUrlToFile } from "../../utils/dataUrlToFile";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import formStyles from "../../components/form/style.module.css";
import styles from "./style.module.css";

const schema = {
  type: "object",
  required: ["name", "email", "phone"],
  properties: {
    files: {
      title: "Avatar",
      type: "string",
    },
    name: {
      title: "Full Name",
      type: "string",
    },
    email: {
      title: "Email Address",
      type: "string",
    },
    phone: {
      title: "Mobile Number",
      type: "string",
    },
    currentPassword: {
      title: "Current Password",
      type: "string",
    },
    newPassword: {
      title: "New Password",
      type: "string",
    },
    confirmPassword: {
      title: "Confirm Password",
      type: "string",
    },
  },
};

const widgets = {
  phone: PhoneNumberInput,
  currentPassword: PasswordInput,
  newPassword: PasswordInput,
  confirmPassword: PasswordInput,
  files: UserFile,
};

function customValidate(formData, errors, uiSchema) {
  if (formData.newPassword !== formData.confirmPassword) {
    errors.confirmPassword.addError("Passwords don't match");
  }
  return errors;
}

const ProfileForm = () => {
  const axiosPrivate = useAxiosPrivate();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [extraErrors, setExtraErrors] = useState({});

  const uiSchema = {
    "ui:classNames": `${formStyles.formInner} ${styles.form_inner}`,
    "ui:rootFieldId": "profile_form",
    files: {
      "ui:classNames": `${formStyles.form_file} ${formStyles.col_12}`,
      "ui:widget": UserFile,
      "ui:options": {
        label: false,
      },
    },
    name: {
      "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
      "ui:placeholder": "eg. john doe",
    },
    email: {
      "ui:classNames": `${formStyles.form_field} ${formStyles.col_6} ${styles.email}`,
      "ui:placeholder": "youremail@gmail.com",
    },
    phone: {
      "ui:widget": PhoneNumberInput,
      "ui:classNames": `${formStyles.form_field} ${formStyles.col_6}`,
      "ui:placeholder": "1345 567-23-56",
    },
    currentPassword: {
      "ui:widget": PasswordInput,
      "ui:classNames": `${formStyles.form_field} ${formStyles.col_4} ${styles.password}`,
      "ui:placeholder": "••••••••",
    },
    newPassword: {
      "ui:widget": PasswordInput,
      "ui:classNames": `${formStyles.form_field} ${formStyles.col_4} ${styles.password}`,
      "ui:placeholder": "••••••••",
    },
    confirmPassword: {
      "ui:widget": PasswordInput,
      "ui:classNames": `${formStyles.form_field} ${formStyles.col_4} ${styles.password}`,
      "ui:placeholder": "••••••••",
    },
    "ui:submitButtonOptions": {
      props: {
        className: formStyles.form_btn,
      },
      submitText: "Save",
    },
  };

  const onFormSubmit = (data) => {
    let tempData = { ...data };
    delete tempData.confirmPassword;
    if (tempData.files) {
      let fileObj = JSON.parse(tempData.files);
      if (fileObj.img) {
        tempData.files = dataUrlToFile(fileObj.img, fileObj.name);
      } else {
        delete tempData.files;
      }
    }
    setFormData(data);
    updateProfile(tempData);
  };

  const fetchProfileInfo = useCallback(async () => {
    try {
      let url = `user/profile`;
      let options = {
        method: "GET",
        url,
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            let profilePictureBuffer =
              response.data?.profilePictureBuffer?.data;
            let fileObj = {
              name: response.data.user.name,
              email: response.data.user.email,
              phone: response.data.user.phone,
            };
            if (profilePictureBuffer && profilePictureBuffer.length > 0) {
              const base64String =
                Buffer.from(profilePictureBuffer).toString("base64");
              let image = `data:image/jpeg;base64,${base64String}`;
              fileObj.img = image;
            }
            let tempData = {
              name: response.data.user.name,
              email: response.data.user.email,
              phone: response.data.user.phone,
              reminderPercentage: response.data.user.reminderPercentage,
              files: JSON.stringify(fileObj),
            };
            setFormData(tempData);
          }
        })
        .catch((err) => {
          if (err.response) {
            // console.error('err.res', err.response.data);
          }
        });
    } catch (error) {
      // console.error('error', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateProfile = useCallback(
    async (data) => {
      setLoading(true);
      setErrorMessage("");
      try {
        let url = `user/update`;
        let options = {
          method: "PUT",
          url,
          data,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
        await axiosPrivate(options)
          .then((response) => {
            if (response.data.status === 1) {
              fetchProfileInfo();
              setExtraErrors({});
            }
            setLoading(false);
          })
          .catch((err) => {
            if (err.response) {
              if (err.response.data.errors) {
                let tempErr = { ...extraErrors };
                for (let key in err.response.data.errors) {
                  tempErr[key] = { __errors: [err.response.data.errors[key]] };
                }
                setExtraErrors(tempErr);
              } else {
                setErrorMessage(err.response.data.message);
                setExtraErrors({});
              }
              setLoading(false);
              // setExtraErrors(err.response.data.errors);
            }
          });
      } catch (error) {
        setErrorMessage("Something went wrong");
        setLoading(false);
      }
    },
    // eslint-disable-next-line  
    []
  );

  useEffect(() => {
    fetchProfileInfo();
  }, [fetchProfileInfo]);

  return (
    <div className={styles.profile_form}>
      <Form
        className={`${formStyles.form}`}
        schema={schema}
        uiSchema={uiSchema}
        widgets={widgets}
        formData={formData}
        validator={validator}
        customValidate={customValidate}
        showErrorList={false}
        extraErrors={extraErrors}
        omitExtraData={true}
        liveValidate={false}
        onSubmit={({ formData }) => {
          onFormSubmit(formData);
        }}
      >
        <div className={styles.form_actions}>
          {errorMessage && <ErrorMessage customClass={styles.error_message_profile} message={errorMessage} />}  <LoadingButton type="submit" loading={loading} title={"Save"} />
        </div>
      </Form>
    </div>
  );
};

export default ProfileForm;
